import React, { useEffect, useMemo, useState } from 'react';
import Spinner from '../common/Spinner';
import useModalClose from '../../hooks/useModalClose';
import { getBolgeCavuslari, talepCavusaIlet, TALEP_DURUMLARI } from '../../services/requestService';

const hataMesaji = (err, varsayilan) =>
  err?.response?.data?.message || err?.response?.data?.mesaj || err?.message || varsayilan;

/**
 * "Bölge Çavuşuna İlet": aktif bölge çavuşları listelenir, aranır, seçilen çavuşa iletilir.
 * Talep zaten bir çavuştaysa aynı pencere çavuşu değiştirir (varsa personel ataması kaldırılır).
 */
const CavusaIletModal = ({ isOpen, talep, onClose, onSuccess }) => {
  const [cavuslar, setCavuslar] = useState([]);
  const [arama, setArama] = useState('');
  const [seciliId, setSeciliId] = useState(null);
  const [not, setNot] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState(null);

  useModalClose(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    setArama('');
    setSeciliId(null);
    setNot('');
    setHata(null);
    setYukleniyor(true);
    getBolgeCavuslari()
      .then((r) => setCavuslar(r.data || []))
      .catch((e) => setHata(hataMesaji(e, 'Çavuş listesi alınamadı.')))
      .finally(() => setYukleniyor(false));
  }, [isOpen]);

  // Çavuş sayısı onlarca; arama istemcide yapılır (API de ?search= destekliyor).
  const filtreli = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase('tr-TR');
    if (!q) return cavuslar;
    return cavuslar.filter((c) =>
      [c.adSoyad, c.ilce, c.mahlist, c.telefon].some((v) => (v || '').toLocaleLowerCase('tr-TR').includes(q))
    );
  }, [cavuslar, arama]);

  if (!isOpen || !talep) return null;

  const secili = cavuslar.find((c) => c.id === seciliId);
  const degistirme = !!talep.cavusId;
  const ayniCavusBekliyor = (c) => c.id === talep.cavusId && talep.durum === TALEP_DURUMLARI.BOLGE_CAVUSUNA_ILETILDI;

  const gonder = async () => {
    if (!secili) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      await talepCavusaIlet({ requestId: talep.requestId, cavusId: secili.id, not: not.trim() || undefined });
      onSuccess(secili);
    } catch (e) {
      setHata(hataMesaji(e, 'Talep iletilemedi.'));
    } finally {
      setGonderiliyor(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="talep-akisi-modal" role="dialog" aria-modal="true" aria-labelledby="cavusa-ilet-baslik">
        <div className="talep-akisi-modal__header">
          <h3 id="cavusa-ilet-baslik">{degistirme ? 'Bölge Çavuşunu Değiştir' : 'Bölge Çavuşuna İlet'}</h3>
          <button type="button" className="talep-akisi-modal__kapat" onClick={onClose} aria-label="Kapat">×</button>
        </div>

        <div className="talep-akisi-modal__body">
          <p className="talep-akisi-modal__ozet">
            #{talep.requestId} · {talep.kategori} · {talep.adres}
          </p>
          {degistirme && (
            <p className="talep-akisi-modal__uyari">
              Şu an: <b>{talep.cavus}</b>
              {talep.personel && <> → <b>{talep.personel}</b>. Personel ataması kaldırılacak.</>}
            </p>
          )}

          <input
            type="text"
            className="talep-akisi-modal__arama"
            placeholder="Çavuş ara (ad, ilçe, mahalle, telefon)"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            autoFocus
          />

          <div className="talep-akisi-modal__liste">
            {yukleniyor ? (
              <div className="talep-akisi-modal__bos"><Spinner size="small" /></div>
            ) : filtreli.length === 0 ? (
              <p className="talep-akisi-modal__bos">Çavuş bulunamadı.</p>
            ) : (
              filtreli.map((c) => (
                <label
                  key={c.id}
                  className={`talep-akisi-modal__secenek${seciliId === c.id ? ' secili' : ''}${ayniCavusBekliyor(c) ? ' pasif' : ''}`}
                >
                  <input
                    type="radio"
                    name="cavus"
                    checked={seciliId === c.id}
                    onChange={() => setSeciliId(c.id)}
                    disabled={ayniCavusBekliyor(c)}
                  />
                  <span className="ad">
                    {c.adSoyad}
                    {c.id === talep.cavusId && <em> (şu anki)</em>}
                  </span>
                  <span className="detay" title={c.mahlist || ''}>{[c.ilce, c.mahlist].filter(Boolean).join(' · ')}</span>
                  <span className="detay">{c.telefon}</span>
                </label>
              ))
            )}
          </div>

          <textarea
            className="talep-akisi-modal__not"
            placeholder="Çavuşa not (isteğe bağlı)"
            maxLength={1000}
            value={not}
            onChange={(e) => setNot(e.target.value)}
          />
          {hata && <p className="error-message">{hata}</p>}
        </div>

        <div className="talep-akisi-modal__footer">
          <button type="button" className="talep-akisi-btn talep-akisi-btn--ikincil" onClick={onClose}>Vazgeç</button>
          <button type="button" className="talep-akisi-btn" onClick={gonder} disabled={!secili || gonderiliyor}>
            {gonderiliyor ? <Spinner size="small" /> : secili ? `${secili.adSoyad} adlı çavuşa ilet` : 'Çavuş seçin'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CavusaIletModal;
